import { InputHTMLAttributes } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

import { BackrsForm } from '../BackrsForm/BackrsForm';
import { ResendCode } from '../BackrsForm/ResendCode';
import { BackrsFormField, BackrsFormValues } from '../BackrsForm/types';

const LOGIN_STRINGS = appStrings.modules.login;

const defaultOtpValues: BackrsFormValues = {
  code: '',
};

type BackToLoginProps = Readonly<{
  handleClick: () => void;
}>;

const BackToLogin = ({ handleClick }: BackToLoginProps) => {
  return (
    <Box
      id="otp-back-to-login"
      data-ph-capture-attribute-name="otp-back-to-login"
      onClick={handleClick}
      sx={backToLoginSx}
    >
      <AppIcon iconName="ArrowLeft" diameter="20px" color="Gray/600" />
      <Typography
        variant="text-sm-semibold"
        color={(theme: Theme) => theme.backrs.colors['Gray/600']}
      >
        {LOGIN_STRINGS.otp.backToLogin}
      </Typography>
    </Box>
  );
};

type OtpProps = Readonly<{
  handleOtpCode: (code: string) => void;
  errorMessage?: string;
  environmentUrl: string;
  phone: string;
  handleBackToLoginNav: () => void;
}>;

export const OneTimePassword = ({
  handleOtpCode,
  errorMessage,
  environmentUrl,
  phone = '555-555-5555',
  handleBackToLoginNav,
}: OtpProps) => {
  const fields: BackrsFormField[] = [
    {
      id: 'code',
      name: 'code',
      placeholder: '######',
      type: 'tel',
      autoComplete: 'code',
      sx: otpInputStyle,
      inputProps: otpInputProps,
    },
  ];

  const handleSubmission = (data: BackrsFormValues) => {
    handleOtpCode(data.code);
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={loginFormStyle}>
        <Box sx={emailIconContainerSx}>
          <AppIcon iconName="Mail01" diameter="28px" color="Colors/Foreground/fg-secondary" />
        </Box>
        <Box sx={textContainerSx}>
          <Typography variant="display-xs-semibold">{LOGIN_STRINGS.otp.title}</Typography>
          <Typography
            variant="text-md-regular"
            color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          >
            {LOGIN_STRINGS.otp.sentCodeTo}
            <br />
            <Typography component="span" variant="text-md-semibold">
              {phone}
            </Typography>
          </Typography>
        </Box>
        <BackrsForm
          fields={fields}
          defaultValues={defaultOtpValues}
          onSubmit={handleSubmission}
          buttonLabel={LOGIN_STRINGS.otp.verifyPhoneNumber}
          buttonId="otp-verify-button"
          variant="otp"
        >
          <Box sx={otpContentWrapperStyle}>
            <ResendCode environmentUrl={environmentUrl} />
            <BackToLogin handleClick={handleBackToLoginNav} />
          </Box>
        </BackrsForm>
        {errorMessage && (
          <Typography
            mt={2}
            variant="text-sm-regular"
            color={(theme) => theme.backrs.colors['Error/600']}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const backToLoginSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-md'],
  cursor: 'pointer',
});

const textContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  textAlign: 'center',
  maxWidth: '300px',
  marginBottom: backrs.spacing['spacing-4xl'],
});

const emailIconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  padding: '14px',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  background: backrs.colors['Colors/Base/White'],
  boxShadow: backrs.shadows['shadow-xs-skeuomorphic'],
  marginBottom: backrs.spacing['spacing-3xl'],
});

const containerStyle: SxProps<Theme> = {
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const loginFormStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: {
    xs: backrs.colors['Colors/Base/Transparent'],
    sm: backrs.colors['Colors/Base/White'],
  },
  paddingX: backrs.spacing['spacing-8xl'],
  paddingY: backrs.spacing['spacing-5xl'],
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 600px)': {
    maxWidth: '500px',
  },
  boxShadow: {
    xs: 'none',
    sm: backrs.shadows['shadow-2xl'],
  },
  border: {
    xs: 'none',
    sm: `3px solid ${backrs.colors['Colors/Base/Black']}`,
  },
});

const otpContentWrapperStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const;

const otpInputStyle: SxProps<Theme> = {
  mb: 4,
} as const;

const otpInputProps: InputHTMLAttributes<HTMLInputElement> = {
  style: { textAlign: 'center', letterSpacing: '0.75rem', fontSize: '1.5rem' },
  maxLength: 6,
};
